import React from 'react';
import {Link} from 'react-router-dom';
import {withCookies} from 'react-cookie';
import '/src/assets/css/page-intro.css';

const PHONE_LABEL = '(713) 715-5051';
const PHONE_HREF = 'tel:+17137155051';
const STORE_TZ = 'America/Chicago';

// Store hours (same schedule as component/Common/Timing.js). Times are 24h "HH:MM".
const HOURS = {
    Mon: ['07:00', '19:00'], Tue: ['07:00', '19:00'], Wed: ['07:00', '19:00'], Thu: ['07:00', '19:00'],
    Fri: ['07:00', '19:00'], Sat: ['07:00', '19:00'], Sun: ['08:00', '16:00'],
};
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const toMinutes = (hhmm) => parseInt(hhmm.slice(0, 2), 10) * 60 + parseInt(hhmm.slice(3), 10);
const label = (hhmm) => {
    const h = parseInt(hhmm.slice(0, 2), 10), m = hhmm.slice(3);
    return `${h % 12 || 12}${m === '00' ? '' : ':' + m} ${h >= 12 ? 'PM' : 'AM'}`;
};

// Open/closed status in the store's own time zone (not the visitor's).
export function getStoreStatus(now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: STORE_TZ, weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(now).reduce((acc, p) => ({...acc, [p.type]: p.value}), {});
    const minutes = (parseInt(parts.hour, 10) % 24) * 60 + parseInt(parts.minute, 10);
    const [open, close] = HOURS[parts.weekday];
    if (minutes >= toMinutes(open) && minutes < toMinutes(close)) {
        return {isOpen: true, text: `Open now · until ${label(close)}`};
    }
    // Closed: find the next opening (later today, or the next day).
    const dayIndex = DAYS.indexOf(parts.weekday);
    if (minutes < toMinutes(open)) {
        return {isOpen: false, text: `Closed · opens ${label(open)}`};
    }
    const nextDay = DAYS[(dayIndex + 1) % 7];
    return {isOpen: false, text: `Closed · opens ${label(HOURS[nextDay][0])} ${nextDay}`};
}

const PhoneIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.58 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
);

// Compact heading banner shown above the product grid on the home and category pages:
// the H1, one line of copy, live open/closed status, a tap-to-call button and the first
// step of ordering (choose pickup or delivery). Height is fixed in CSS so it never shifts.
class PageIntro extends React.Component {
    state = {status: null};

    componentDidMount() {
        this.updateStatus();
        this.timer = setInterval(this.updateStatus, 60000);
    }

    componentWillUnmount() {
        clearInterval(this.timer);
    }

    // Status depends on the current time, so it is computed in the browser only (keeps SSR/hydration identical).
    updateStatus = () => this.setState({status: getStoreStatus()});

    openStoreChooser = () => {
        this.props.cookies.set('store_modal', true, {path: '/'});
    };

    render() {
        const {title, children} = this.props;
        const {status} = this.state;
        return (
            <section className="page-intro">
                <div className="page-intro__main">
                    <h1 className="page-intro__title">{title}</h1>
                    <p className="page-intro__text">{children}</p>
                    <ul className="page-intro__perks">
                        <li>Skip the line</li>
                        <li>Pickup or delivery</li>
                        <li>Same prices as in store</li>
                    </ul>
                </div>
                <div className="page-intro__side">
                    <button type="button" className="page-intro__btn" onClick={this.openStoreChooser}>
                        Choose pickup or delivery
                    </button>
                    <div className="page-intro__contact">
                        <span className={"page-intro__status" + (status ? (status.isOpen ? " is-open" : " is-closed") : "")}
                              aria-live="polite">
                            {status ? status.text : ' '}
                        </span>
                        <a className="page-intro__call" href={PHONE_HREF} aria-label={"Call Farmer's Fresh Meat at " + PHONE_LABEL}>
                            <PhoneIcon/>{PHONE_LABEL}
                        </a>
                    </div>
                    <Link className="page-intro__stores-link" to="/locations-contact">Store addresses &amp; directions</Link>
                </div>
            </section>
        );
    }
}

export default withCookies(PageIntro);
